import {
  CreateAuctionProposalDto,
  CreditAuctionProposal,
  CreditAuctionProposalListParams,
  CreditAuctionProposalListResponse,
  FindOneCompleteAuctionProposalDto,
  ResponseAuctionProposalDto,
  UpdateAuctionProposalDto,
} from "@/models/credit-auction-proposal.model";
import { axiosLeilaoConsignadoTrabalhadorInstance } from "./axiosInstance";
import { CreditEngineReportResponseData } from "@/models/consignado/report-data.model";
import { parseEmployFullDocumentByType } from "@/utils/consignado/employerFullDocument.utils";
import {
  getMockAuctionProposalDetail,
  getMockAuctionProposalEngineReport,
  getMockAuctionProposalsList,
} from "../mocks/creditAuctionProposal.mock";

const USE_MOCK_AUCTION_PROPOSALS = import.meta.env.VITE_USE_AUCTION_MOCKS === "true";

const getNumberFromUnknown = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

const getStringFromUnknown = (value: unknown, fallback: string): string => {
  if (typeof value === "string" && value.trim()) {
    return value;
  }

  if (typeof value === "number") {
    return String(value);
  }

  return fallback;
};

const getField = <T>(
  source: Record<string, unknown> | null,
  keys: string[],
  fallback: T
): T => {
  if (!source) {
    return fallback;
  }

  for (const key of keys) {
    if (key in source) {
      return source[key] as T;
    }
  }

  return fallback;
};

const mapDtoToUiProposal = (dto: ResponseAuctionProposalDto): CreditAuctionProposal => {
  return {
    id: dto.id,
    Status: dto.status,
    CustomStatus: dto.statusCustomizado,
    IdSolicitacao: dto.numeroSolicitacao,
    ShortHash: dto.shortHash,
    CreatedAt: dto.createdAt,
    UrlAutocontratacao: null,
    Cpf: dto.documentoTomador,
    Telefone: "Não informado",
    Email: "Não informado",
    Idade: null,
    Sexo: "Não informado",
    Matricula: "Não informado",
    InscricaoEmpregador: {
      Codigo: 0,
      Descricao: "Não informado",
    },
    NumeroInscricaoEmpregador: "Não informado",
    ValorLiberado: dto.valorLiberado,
    NroParcelas: dto.numeroParcelas,
    TaxaMensal: dto.valorTaxaMensal,
    Produto: "Não informado",
    DataDesembolso: dto.dataDesembolso,
    DataHoraValidadeSolicitacao: dto.dataHoraValidadeSolicitacao,
    NomeTrabalhador: "Não informado",
    DataNascimento: "",
    MargemDisponivel: dto.margem ?? 0,
    MargemReal: 0,
    TotalVencimentos: 0,
    ElegivelEmprestimo: false,
    PessoaExpostaPoliticamente: {
      Codigo: 0,
      Descricao: "Não informado",
    },
    DataAdmissao: "",
  };
};

const mapCompleteDtoToUiProposal = (
  dto: FindOneCompleteAuctionProposalDto
): CreditAuctionProposal => {
  const baseProposal = mapDtoToUiProposal(dto.proposal);

  const naturalPerson = dto.naturalPerson;
  const employer = dto.employer;
  const chosenFinancialInfo = dto.chosenFinancialInfo;
  // margin contém admissionDate e availableMargin

  const personName = getStringFromUnknown(
    getField<unknown>(naturalPerson, ["name", "nome", "fullName"], null),
    baseProposal.NomeTrabalhador
  );

  const personDocument = getStringFromUnknown(
    getField<unknown>(naturalPerson, ["document", "cpf", "documento"], null),
    baseProposal.Cpf
  );

  const personBirthDate = getStringFromUnknown(
    getField<unknown>(naturalPerson, ["birthDate", "birth_date", "dataNascimento"], null),
    "Não informado"
  );

  const personMatricula = getStringFromUnknown(
    getField<unknown>(naturalPerson, ["registration", "matricula"], null),
    "Não informado"
  );

  const personPhone = getStringFromUnknown(
    getField<unknown>(naturalPerson, ["phoneNumber", "phone", "telefone"], null),
    "Não informado"
  );

  const personEmail = getStringFromUnknown(
    getField<unknown>(naturalPerson, ["email"], null),
    "Não informado"
  );

  const personGender = getStringFromUnknown(
    getField<unknown>(naturalPerson, ["gender", "sex", "sexo", "genero"], null),
    "Não informado"
  );

  const personAge = (() => {
    if (!personBirthDate) return null;
    const birth = new Date(personBirthDate);
    if (Number.isNaN(birth.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    return age;
  })();

  const employerName = getStringFromUnknown(
    getField<unknown>(employer, ["corporateName", "name", "razaoSocial", "nome", "employerName"], null),
    "Não informado"
  );

  // const employerDocument = getStringFromUnknown(
  //   getField<unknown>(employer, ["document", "cnpj", "numeroInscricao"], null),
  //   "Não informado"
  // );
  
  const employerFullDocument = parseEmployFullDocumentByType(employer?.cnpj_basico as string, employer?.cnpj_ordem as string, employer?.cnpj_dv as string)

  const employerDocumentType = getStringFromUnknown(
    getField<unknown>(employer, ["documentType", "tipoDocumento", "tipoInscricao"], null),
    "Não informado"
  );

  const admissionDate = getStringFromUnknown(
    getField<unknown>(chosenFinancialInfo, ["admissionDate", "dataAdmissao"], null),
    "Não informado"
  );

  const margemDisponivel = getNumberFromUnknown(
    getField<unknown>(chosenFinancialInfo, ["releasedAmount", "availableMargin", "margemDisponivel"], null),
    baseProposal.MargemDisponivel
  );

  const margemReal = getNumberFromUnknown(
    getField<unknown>(
      chosenFinancialInfo,
      ["realMargin", "baseMargin", "marginBase", "valorBaseMargem", "margemReal", "margemBase", "valor_base_margem"],
      null
    ),
    baseProposal.MargemReal
  );

  const totalVencimentos = getNumberFromUnknown(
    getField<unknown>(
      chosenFinancialInfo,
      ["totalGrossAmount", "totalEarnings", "totalVencimentos", "valorTotalVencimentos", "valor_total_vencimentos"],
      null
    ),
    baseProposal.TotalVencimentos
  );

  const productEntity = getField<Record<string, unknown> | null>(
    chosenFinancialInfo,
    ["Product", "product", "produto"],
    null
  );

  const productName = getStringFromUnknown(
    getField<unknown>(productEntity, ["name", "nome", "displayName"], null),
    baseProposal.Produto
  );

  const pep = getField<unknown>(
    naturalPerson,
    ["politicallyExposedPerson", "politicallyExposed", "isPoliticallyExposed", "pep"],
    null
  );

  const pepInfo: CreditAuctionProposal["PessoaExpostaPoliticamente"] =
    pep === null
      ? baseProposal.PessoaExpostaPoliticamente
      : (() => {
          const isPep =
            typeof pep === "boolean" ? pep : getNumberFromUnknown(pep, 0) > 0;
          return {
            Codigo: isPep ? 1 : 0,
            Descricao: isPep
              ? "Pessoa Exposta Politicamente"
              : "Pessoa Não Exposta Politicamente",
          };
        })();

  return {
    ...baseProposal,
    NomeTrabalhador: personName,
    Cpf: personDocument,
    Telefone: personPhone,
    Email: personEmail,
    Idade: personAge,
    Sexo: personGender,
    DataNascimento: personBirthDate,
    Matricula: personMatricula,
    NumeroInscricaoEmpregador: employerFullDocument,
    MargemDisponivel: margemDisponivel,
    MargemReal: margemReal,
    TotalVencimentos: totalVencimentos,
    Produto: productName,
    PessoaExpostaPoliticamente: pepInfo,
    DataAdmissao: admissionDate,
    InscricaoEmpregador: {
      Codigo: employerDocumentType === "CNPJ" ? 1 : 0,
      Descricao: employerName,
    },
    UrlAutocontratacao: dto.urlAutocontratacao ?? null,
  };
};

export async function getCreditAuctionProposals(
  params: CreditAuctionProposalListParams
): Promise<CreditAuctionProposalListResponse> {
  if (USE_MOCK_AUCTION_PROPOSALS) {
    return getMockAuctionProposalsList(params);
  }

  const queryParams = new URLSearchParams();

  queryParams.set("pagina", String(params.page));
  queryParams.set("tamanho_pagina", String(params.pageSize));

  if (params.searchTerm?.trim()) {
    queryParams.set("search_term", params.searchTerm.trim());
  }

  if (params.statuses?.length) {
    queryParams.set("status", params.statuses.join(","));
  }

  if (typeof params.wonAuction === "boolean") {
    queryParams.set("ganhamos_leilao", String(params.wonAuction));
  }

  if (params.startDate) {
    queryParams.set("data_inicial", params.startDate);
  }

  if (params.endDate) {
    queryParams.set("data_final", params.endDate);
  }

  if (params.validadeStartDate) {
    queryParams.set("validade_inicial", params.validadeStartDate);
  }

  if (params.validadeEndDate) {
    queryParams.set("validade_final", params.validadeEndDate);
  }

  if (
    typeof params.minReleasedValue === "number" &&
    Number.isFinite(params.minReleasedValue)
  ) {
    queryParams.set("valor_liberado_min", String(params.minReleasedValue));
  }

  if (
    typeof params.maxReleasedValue === "number" &&
    Number.isFinite(params.maxReleasedValue)
  ) {
    queryParams.set("valor_liberado_max", String(params.maxReleasedValue));
  }

  if (params.cpf?.trim()) {
    queryParams.set("cpf", params.cpf.trim());
  }

  if (params.negotiationSubStep) {
    queryParams.set("sub_step_negociacao", params.negotiationSubStep);
  }

  if (params.sortBy) {
    queryParams.set("ordenar_por", params.sortBy);
  }

  if (params.sortDirection) {
    queryParams.set("direcao", params.sortDirection);
  }

  const { data } =
    await axiosLeilaoConsignadoTrabalhadorInstance.get<CreditAuctionProposalListResponse>(
      "/api-consignado-trabalhador/v1/proposta-leilao",
      {
        params: queryParams,
      }
    );

  return data;
}

export async function getCreditAuctionProposalComplete(
  id: string
): Promise<CreditAuctionProposal> {
  if (USE_MOCK_AUCTION_PROPOSALS) {
    const mock = getMockAuctionProposalDetail(id);
    if (!mock) {
      throw new Error(`Proposta mock não encontrada para id: ${id}`);
    }
    console.log(`[Leilão][MOCK] Detalhes da proposta ${id}:`, mock);
    return mock;
  }

  const { data } =
    await axiosLeilaoConsignadoTrabalhadorInstance.get<FindOneCompleteAuctionProposalDto>(
      `/api-consignado-trabalhador/v1/proposta-leilao/completo/${id}`
    );

  console.log(`[Leilão] Detalhes da proposta ${id} - raw response:`, data);
  console.log(`[Leilão] proposal:`, data?.proposal);
  console.log(`[Leilão] naturalPerson:`, data?.naturalPerson);
  console.log(`[Leilão] chosenFinancialInfo:`, data?.chosenFinancialInfo);
  console.log(`[Leilão] employer:`, data?.employer);
  console.log(`[Leilão] processor:`, data?.processor);
  console.log(`[Leilão] bankDetail:`, data?.bankDetail);
  console.log(`[Leilão] unicoData:`, data?.unicoData);

  const mapped = mapCompleteDtoToUiProposal(data);
  console.log(`[Leilão] UI proposal mapeada (o que vai pra tela):`, mapped);

  return mapped;
}

export async function createCreditAuctionProposal(
  dto: CreateAuctionProposalDto
): Promise<ResponseAuctionProposalDto> {
  const { data } =
    await axiosLeilaoConsignadoTrabalhadorInstance.post<ResponseAuctionProposalDto>(
      "/api-consignado-trabalhador/v1/proposta-leilao",
      dto
    );

  return data;
}

export async function updateCreditAuctionProposal(params: {
  id: string;
  dto: UpdateAuctionProposalDto;
}): Promise<ResponseAuctionProposalDto> {
  const { data } =
    await axiosLeilaoConsignadoTrabalhadorInstance.patch<ResponseAuctionProposalDto>(
      `/api-consignado-trabalhador/v1/proposta-leilao/${params.id}`,
      params.dto
    );

  return data;
}

export async function deleteCreditAuctionProposal(id: string): Promise<void> {
  await axiosLeilaoConsignadoTrabalhadorInstance.delete(`/api-consignado-trabalhador/v1/proposta-leilao/${id}`);
}

export async function getCreditEngineAuctionProposalReport(
  id: string
): Promise<CreditEngineReportResponseData> {
  if (USE_MOCK_AUCTION_PROPOSALS) {
    return getMockAuctionProposalEngineReport();
  }

  const { data } =
    await axiosLeilaoConsignadoTrabalhadorInstance.get<any>(
      `/api-consignado-trabalhador/v1/credit-engine-report/proposta-leilao/${id}`
    );

  console.log(`[Leilão] Credit engine report ${id}:`, data);

  const vinculo = data?.vinculos_empregaticios?.[0];
  const dadosPessoais = data?.dados_pessoais;

  const totalVencimentosRaw = vinculo?.total_vencimentos;
  const totalVencimentos =
    totalVencimentosRaw == null
      ? null
      : typeof totalVencimentosRaw === "number"
      ? totalVencimentosRaw
      : Number(totalVencimentosRaw);

  return {
    proposta: { id: '', status: '', short_hash: '', data_criacao: '' },
    empregador: vinculo?.empregador || { nome: 'Desconhecido' },
    relatorios: vinculo?.relatorios || [],
    // Passos da análise de crédito do vínculo: sem eles o relatório cai no layout antigo.
    analise_credito: vinculo?.analise_credito ?? null,
    fallback: {
      nome: dadosPessoais?.nome ?? null,
      cpf: dadosPessoais?.cpf ?? null,
      email: dadosPessoais?.email ?? null,
      telefone: dadosPessoais?.telefone ?? null,
      idade: typeof dadosPessoais?.idade === "number" ? dadosPessoais.idade : null,
      sexo: dadosPessoais?.genero ?? null,
      dataNascimento: dadosPessoais?.data_nascimento ?? null,
      empregadorNome: vinculo?.empregador?.nome ?? null,
      empregadorCnpj: vinculo?.empregador?.cnpj ?? null,
      dataAdmissao: vinculo?.data_admissao ?? null,
      margemReal: typeof vinculo?.margem === "number" ? vinculo.margem : null,
      margemDisponivel:
        typeof vinculo?.margem_disponivel === "number" ? vinculo.margem_disponivel : null,
      totalVencimentos: Number.isFinite(totalVencimentos as number) ? totalVencimentos : null,
    },
  };
}
